/**
 * A floating panel anchored to a trigger: the launcher, quick create, the
 * notification center and the account menu are all one of these.
 *
 * Unlike Menu, a click inside does NOT close it — the account menu holds a
 * theme switch that has to stay open while you try each option. Items that
 * should close it call the `close` passed to `children` (or carry
 * `data-close`), and so do links, because a link means you are going
 * somewhere.
 *
 * Accessibility is the substance: the trigger reports `aria-expanded`, focus
 * moves into the panel on open and back to the trigger on close, Escape
 * closes, and inside a `role="menu"` panel the arrow keys move between items.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const ITEMS = '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"], [role="option"]';

export function Popover({
  trigger,
  children,
  align = 'start',
  label,
  role = 'dialog',
  className = '',
  panelClassName = '',
  open: controlledOpen,
  onOpenChange,
  initialFocus,
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : uncontrolledOpen;

  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const id = useId();

  const setOpen = useCallback(
    (next) => {
      if (!controlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange]
  );

  const close = useCallback(
    ({ restoreFocus = true } = {}) => {
      setOpen(false);
      if (restoreFocus) triggerRef.current?.focus?.();
    },
    [setOpen]
  );

  // Outside press and Escape.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!anchorRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpen, close]);

  // Focus into the panel once it is on screen.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    // Content that took focus itself (children's effects run first) keeps it.
    if (panel.contains(document.activeElement)) return;
    // 'panel' hands focus to the panel itself and lets its content take it
    // from there (the launcher focuses its own search field when it mounts).
    const target =
      (initialFocus === 'panel' && panel) ||
      (initialFocus && initialFocus !== 'panel' && panel.querySelector(initialFocus)) ||
      panel.querySelector('[data-autofocus]') ||
      panel.querySelector(ITEMS) ||
      panel.querySelector(FOCUSABLE) ||
      panel;
    target?.focus?.({ preventScroll: true });
  }, [open, initialFocus]);

  const onPanelKeyDown = (event) => {
    if (role !== 'menu') return;
    const items = [...(panelRef.current?.querySelectorAll(ITEMS) ?? [])].filter((node) => !node.disabled);
    if (!items.length) return;

    const index = items.indexOf(document.activeElement);
    const moves = { ArrowDown: 1, ArrowUp: -1 };
    if (event.key in moves) {
      event.preventDefault();
      const next = index < 0 ? 0 : (index + moves[event.key] + items.length) % items.length;
      items[next].focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      items[0].focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      items.at(-1).focus();
    }
  };

  // Links and explicit closers dismiss the panel; everything else keeps it.
  const onPanelClick = (event) => {
    const closer = event.target.closest?.('a[href], [data-close]');
    if (closer && panelRef.current?.contains(closer)) setOpen(false);
  };

  const triggerProps = {
    ref: triggerRef,
    'aria-haspopup': role === 'menu' ? 'menu' : 'dialog',
    'aria-expanded': open,
    'aria-controls': open ? id : undefined,
    onClick: () => setOpen(!open),
  };

  return (
    <div className={`popover-anchor ${className}`} ref={anchorRef}>
      {trigger({ open, toggle: () => setOpen(!open), close, triggerProps })}
      {open && (
        <div
          ref={panelRef}
          id={id}
          className={`popover popover-${align} ${panelClassName}`}
          role={role}
          aria-label={label}
          tabIndex={-1}
          onKeyDown={onPanelKeyDown}
          onClick={onPanelClick}
        >
          {typeof children === 'function' ? children({ close }) : children}
        </div>
      )}
    </div>
  );
}
